<template>
  <div v-if="visible" class="mb-2">
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between">
      <span class="text-caption font-weight-bold grey--text">First Click Attribution</span>
      <span class="text-caption grey--text">{{ dateRangeLabel }}</span>
    </div>
    <v-card flat class="mt-2 py-5 px-6" :to="reportRoute">
      <!-- 4 across on lg+, 2x2 on sm-md, stacked on xs. Pairs keep the divider
           orientation right at every breakpoint. -->
      <div class="d-flex flex-column flex-lg-row">
        <template v-for="(pair, pairIndex) in cardPairs">
          <v-divider v-if="pairIndex" :key="'pair-vdivider-' + pairIndex" vertical class="d-none d-lg-block revenue-divider mx-6" />
          <v-divider v-if="pairIndex" :key="'pair-hdivider-' + pairIndex" class="d-lg-none my-4" />
          <div :key="'pair-' + pairIndex" class="revenue-pair d-flex flex-column flex-sm-row">
            <template v-for="(card, index) in pair">
              <v-divider v-if="index" :key="card.key + '-vdivider'" vertical class="d-none d-sm-block revenue-divider mx-6" />
              <v-divider v-if="index" :key="card.key + '-hdivider'" class="d-sm-none my-4" />
              <div :key="card.key" class="revenue-section d-flex align-center">
                <v-avatar rounded="lg" size="52" :color="card.tonal" class="mr-3">
                  <v-icon :color="card.color">{{ card.icon }}</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text text--darken-1">{{ card.label }}</div>
                  <div v-if="loading" class="d-flex align-center revenue-skeleton" style="height: 2rem;">
                    <v-skeleton-loader type="text" width="90" />
                  </div>
                  <div v-else class="text-h5 font-weight-bold">
                    {{ formatValue(card) }}
                  </div>
                  <!-- Shown beneath the AI figure, never added in: assisted orders are
                       already counted under Paid or Direct. -->
                  <v-tooltip v-if="!loading && card.note" bottom max-width="300">
                    <template v-slot:activator="{ on }">
                      <div class="text-caption grey--text revenue-note" v-on="on">
                        {{ card.note }}
                      </div>
                    </template>
                    <span>{{ card.noteTooltip }}</span>
                  </v-tooltip>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  data () {
    // Same month-period default as the Organic Revenue report: the complete days of
    // the current month (excluding today, which is incomplete). On the 1st there are
    // no complete days yet, so fall back to the previous full month.
    const today = dayjs()
    let start, end
    if (today.date() === 1) {
      const prevMonth = today.subtract(1, 'month')
      start = prevMonth.startOf('month')
      end = prevMonth.endOf('month')
    } else {
      start = today.startOf('month')
      end = today.subtract(1, 'day')
    }
    return {
      startDate: start,
      endDate: end,
      loading: false,
      failed: false,
      summary: null,
      reportRoute: { name: 'OrganicRevenue' }
    }
  },
  computed: {
    dateRangeLabel () {
      return `${this.startDate.format('MMM D')} - ${this.endDate.format('MMM D, YYYY')}`
    },
    cards () {
      const total = this.summary?.total?.revenue || 0
      const organic = this.summary?.organic?.revenue || 0
      const ai = this.summary?.ai?.revenue || 0
      const assisted = this.summary?.ai?.assistedRevenue || 0
      const assistedOrders = this.summary?.ai?.assistedOrders || 0
      const searchRevenue = organic + ai
      return [
        { key: 'total', label: 'Total Revenue', icon: 'fa-solid fa-sack-dollar', color: 'primary', tonal: '#e9eef5', value: total },
        { key: 'organic', label: 'Organic Revenue', icon: 'fa-solid fa-leaf', color: 'success', tonal: '#e7f3e8', value: organic },
        {
          key: 'ai',
          label: 'AI Revenue',
          icon: 'fa-solid fa-robot',
          color: 'purple',
          tonal: '#f2e8f5',
          value: ai,
          note: assisted ? `+${this.formatCurrency(assisted)} AI-assisted` : '',
          noteTooltip: assisted
            ? `${assistedOrders} order${assistedOrders === 1 ? '' : 's'} where GA4 saw an AI assistant in the journey but the order's own landing data credits another channel. That revenue is counted under Paid or Direct, so it is shown separately rather than added to AI Revenue.`
            : ''
        },
        {
          key: 'ratio',
          label: 'Organic + AI / Total',
          icon: 'fa-solid fa-percent',
          color: 'teal',
          tonal: '#e3f1f1',
          value: total ? Math.round((searchRevenue / total) * 100) : null,
          format: 'percent'
        }
      ]
    },
    cardPairs () {
      return [this.cards.slice(0, 2), this.cards.slice(2)]
    },
    hasData () {
      return !!this.summary?.total?.revenue
    },
    // Hide the whole section when the account has no revenue for the period (or the
    // request failed) — an all-zero card row is just noise on the dashboard.
    visible () {
      return !this.failed && (this.loading || this.hasData)
    }
  },
  methods: {
    formatValue (card) {
      if (card.format === 'percent') {
        return card.value === null ? '—' : `${card.value}%`
      }
      return this.formatCurrency(card.value)
    },
    formatCurrency (cents) {
      const dollars = cents / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(dollars)
    },
    async loadSummary () {
      this.loading = true
      try {
        const dateRange = `${this.startDate.format('YYYY-MM-DD')},${this.endDate.format('YYYY-MM-DD')}`
        const response = await this.$http.get('/api/seo-analytics/summary', {
          params: { dateRange, compare: 'none' },
          hideErrors: true
        })
        this.summary = response.data
      } catch (e) {
        this.failed = true
      } finally {
        this.loading = false
      }
    }
  },
  created () {
    this.loadSummary()
  }
}
</script>

<style scoped>
/* Equal-width segments, left-aligned content. flex-basis 0 only applies in the
   row layouts — in the stacked (column) layouts it would collapse the heights. */
.revenue-section,
.revenue-pair {
  min-width: 0;
}
@media (min-width: 600px) {
  .revenue-section {
    flex: 1 1 0;
  }
}
@media (min-width: 1264px) {
  .revenue-pair {
    flex: 1 1 0;
  }
}
/* Vuetify's vertical divider sizes itself with min-height: 100%, which resolves
   to 0 inside a flex row with no explicit height — stretch it instead. */
.revenue-divider {
  align-self: stretch;
  height: auto;
  min-height: 0;
}
/* Size the skeleton bone to the text-h5 value it stands in for (24px text in a
   2rem line) so the card doesn't shift when the numbers load in. */
.revenue-skeleton ::v-deep .v-skeleton-loader__text {
  height: 24px;
  margin-bottom: 0;
}
</style>
